
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Message } from '@/types/insurance';
import { detectLanguage } from '@/lib/languageUtils';
import { messageContainsEmailOrTemplate } from '@/lib/utils';

interface UseTemplateChangesProps {
  onEmailGenerated: (content: string) => void;
  processMessage: (content: string, contextType: string, options: any) => Promise<Message | undefined>;
}

interface PendingTemplateChange {
  messageId: string;
  content: string;
}

export const useTemplateChanges = ({ onEmailGenerated, processMessage }: UseTemplateChangesProps) => {
  const [templateChangeRequest, setTemplateChangeRequest] = useState<string | null>(null);
  const [templateChangeDescription, setTemplateChangeDescription] = useState<string | null>(null);
  const [templateChangeConfirmed, setTemplateChangeConfirmed] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pendingTemplateChanges, setPendingTemplateChanges] = useState<PendingTemplateChange | null>(null);
  const { toast } = useToast();

  // Describe template changes based on the request
  const describeTemplateChanges = async (request: string) => {
    try {
      // Generate a description of the changes from the request
      const summary = request.length > 100 
        ? request.substring(0, 100) + '...' 
        : request;
      
      setTemplateChangeDescription(`Template ändern: ${summary}`);
      
      // This could be enhanced to use AI to generate better summaries
      return summary;
    } catch (error) {
      console.error('Failed to describe template changes:', error);
      setTemplateChangeDescription('Änderungsvorschlag für Template');
      return null;
    }
  };

  // Save template version to history
  const saveTemplateVersion = async (content: string) => {
    try {
      console.log('Template version saved, length:', content.length);
      // Implementation would depend on your template versioning system
      return true;
    } catch (error) {
      console.error('Failed to save template version:', error);
      return false;
    }
  };

  // Get template version history
  const getTemplateVersions = async () => {
    try {
      // Implementation would depend on your template versioning system
      return [];
    } catch (error) {
      console.error('Failed to get template versions:', error);
      return [];
    }
  };

  const confirmAndProcessTemplateChange = async () => {
    if (!templateChangeRequest) {
      console.error("No template change request provided");
      return;
    }
    
    try {
      setTemplateChangeConfirmed(true);
      setIsGenerating(true);
      
      const aiMessage = await processMessage(
        templateChangeRequest,
        'email',
        {
          language: detectLanguage(templateChangeRequest) || 'de',
          format: 'html',
          contextType: 'email',
          emailStage: 'template'
        }
      );
      
      if (aiMessage && messageContainsEmailOrTemplate(aiMessage.content)) {
        console.log("Generated template content length:", aiMessage.content.length);
        onEmailGenerated(aiMessage.content);
        
        // Save template version after successful generation
        await saveTemplateVersion(aiMessage.content);
      } else {
        toast({
          title: "Fehler",
          description: "Die generierte Vorlage ist ungültig oder leer.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Failed to update template:', error);
      toast({
        title: "Fehler",
        description: "Die Änderung des Templates konnte nicht durchgeführt werden.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
      setTemplateChangeRequest(null);
      setTemplateChangeDescription(null);
      setPendingTemplateChanges(null);
    }
  };

  const cancelTemplateChange = () => {
    setTemplateChangeRequest(null);
    setTemplateChangeDescription(null);
    setTemplateChangeConfirmed(false);
    setPendingTemplateChanges(null);
  };

  return {
    templateChangeRequest,
    setTemplateChangeRequest,
    templateChangeDescription,
    setTemplateChangeDescription,
    templateChangeConfirmed,
    setTemplateChangeConfirmed,
    isGenerating,
    confirmAndProcessTemplateChange,
    cancelTemplateChange,
    // Add the previously missing properties
    pendingTemplateChanges,
    setPendingTemplateChanges,
    describeTemplateChanges,
    saveTemplateVersion,
    getTemplateVersions
  };
};
